import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogIn } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';

const GOOGLE_AUTH_URL = 'https://auth.emergentagent.com/';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { branding } = useBranding();
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Enter email and password');
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back!');
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Login failed. Check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `${GOOGLE_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${branding.primary_color || '#0f2a5a'} 0%, #1a3a7a 100%)`,
      padding: '20px',
    }} data-testid="login-page">
      <div style={{
        background: 'white', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={branding.logo_url || '/gnosis-logo.png'} alt="Logo"
               style={{ height: '56px', marginBottom: '16px', objectFit: 'contain' }}
               onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 style={{ fontSize: '20px', fontWeight: 600, color: branding.primary_color || '#0f2a5a', margin: 0 }}>
            {branding.app_name}
          </h1>
          <p style={{ fontSize: '13px', color: '#666', margin: '8px 0 0' }}>Contract Registry — Sign In</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@company.com" required data-testid="login-email" autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="••••••••" required data-testid="login-password" autoComplete="current-password" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="login-submit"
                  style={{ backgroundColor: branding.primary_color || '#0f2a5a', color: 'white', marginTop: '8px' }}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
            {submitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 12px' }}>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
          <span style={{ fontSize: 11, color: '#888' }}>OR</span>
          <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
        </div>

        <button type="button" onClick={handleGoogleLogin} data-testid="google-login-btn"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '10px 16px', border: '1px solid #dadce0', background: 'white', borderRadius: 6,
                  fontSize: 14, fontWeight: 500, color: '#3c4043', cursor: 'pointer',
                }}>
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
            <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
            <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
            <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
          </svg>
          Sign in with Google
        </button>

        <p style={{ fontSize: '11px', color: '#888', textAlign: 'center', marginTop: '24px' }}>
          Access is invite-only. Contact your Super Admin to get an account.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
